import { ReactNode, useEffect, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { ModalContainer } from "./ModalStyles";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/store";
import { addToStack, removeFromStack } from "../../../reducers/modals";

interface ModalProps {
  children: ReactNode;
  isOpen?: boolean;
  onRequestClose?: () => void;
}

function subscribeToNothing() {
  return () => {};
}

function getClientSnapshot() {
  return true;
}

function getServerSnapshot() {
  return false;
}

// True once rendering on the client, false during server rendering and
// hydration. Lets the portal target document.body without a mismatch.
function useIsClient() {
  return useSyncExternalStore(
    subscribeToNothing,
    getClientSnapshot,
    getServerSnapshot,
  );
}

export default function Modal({
  children,
  isOpen,
  onRequestClose,
}: ModalProps) {
  const dispatch = useAppDispatch();
  const modalIdRef = useRef(uuidv4());
  const stack = useAppSelector((state) => state.modals.stack);
  const isClient = useIsClient();

  useEffect(() => {
    const modalId = modalIdRef.current;
    if (isOpen) {
      dispatch(addToStack({ modalId: modalId }));
    } else {
      dispatch(removeFromStack({ modalId: modalId }));
    }
    return () => {
      dispatch(removeFromStack({ modalId: modalId }));
    };
  }, [dispatch, isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        onRequestClose !== undefined &&
        stack.at(-1) == modalIdRef.current
      ) {
        onRequestClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onRequestClose, stack]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen || !isClient) {
    return null;
  }
  // Rendered onto the body rather than in place. A fixed-position element is
  // positioned relative to its nearest ancestor with a filter or transform,
  // and the rule checks table applies a filter while checks are running,
  // which otherwise traps the modal inside that scrolling table.
  return createPortal(
    <ModalContainer>{children}</ModalContainer>,
    document.body,
  );
}
